import React, { useState } from "react";
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [modalMessage, setModalMessage] = useState({ type: '', text: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setModalMessage({ type: '', text: '' });
    
    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Mesaj gönderilirken bir hata oluştu.");
      }

      const result = await response.json();
      setStatus("success");
      setModalMessage({ type: 'success', text: result.message });
      setIsModalOpen(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      setStatus("error");
      setModalMessage({ type: 'error', text: error.message });
      setIsModalOpen(true);
      console.error("Mesaj gönderme hatası:", error);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleFillEmailFromSession = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Giriş yapmadınız veya oturumunuz bulunamadı.");
      return;
    }

    try {
      const base64Payload = token.split(".")[1]; 
      const decodedPayload = JSON.parse(atob(base64Payload));

      if (decodedPayload?.sub) {
        setFormData(prev => ({ ...prev, email: decodedPayload.sub }));
      } else {
        alert("JWT içinde e-posta bilgisi bulunamadı.");
      }
    } catch (err) {
      console.error("Token çözümleme hatası:", err);
      alert("Oturum bilgisi okunamadı.");
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 min-h-screen font-sans text-gray-50">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* === Sayfa Başlığı === */}
        <header className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-500 tracking-tight">
            Bize Ulaşın
          </h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            Sorularınız, önerileriniz ve işbirliği talepleriniz için buradayız.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* === İletişim Formu === */}
          <section className="bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-700">
            <div className="flex items-center gap-4 mb-6">
              <Mail className="h-8 w-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-gray-50">İletişim Formu</h2>
            </div>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-400">Adınız Soyadınız</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="mt-1 block w-full px-4 py-3 bg-gray-700 text-gray-200 rounded-xl shadow-inner border border-gray-600 focus:ring-2 focus:ring-blue-400 focus:outline-none transition-colors"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-400">E-posta Adresiniz</label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="flex-grow block w-full px-4 py-3 bg-gray-700 text-gray-200 rounded-xl shadow-inner border border-gray-600 focus:ring-2 focus:ring-blue-400 focus:outline-none transition-colors"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={handleFillEmailFromSession}
                    className="px-4 py-2 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors text-sm"
                  >
                    E-postamı Doldur
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-400">Konu</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="mt-1 block w-full px-4 py-3 bg-gray-700 text-gray-200 rounded-xl shadow-inner border border-gray-600 focus:ring-2 focus:ring-blue-400 focus:outline-none transition-colors"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-400">Mesajınız</label>
                <textarea
                  id="message"
                  name="message"
                  className="mt-1 block w-full px-4 py-3 bg-gray-700 text-gray-200 rounded-xl shadow-inner border border-gray-600 focus:ring-2 focus:ring-blue-400 focus:outline-none transition-colors"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl shadow-lg hover:bg-blue-600 transition-colors duration-300 disabled:bg-gray-600 disabled:cursor-not-allowed"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Gönderiliyor...
                  </>
                ) : (
                  "Mesajı Gönder"
                )}
              </button>
            </form>
          </section>

          {/* === Diğer İletişim Kanalları === */}
          <section className="bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-700 h-fit">
            <div className="flex items-center gap-4 mb-6">
              <Mail className="h-8 w-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-gray-50">Diğer İletişim Kanalları</h2>
            </div>
            <div className="space-y-6 text-gray-300 text-lg">
              <div className="flex items-start gap-3">
                <span className="font-semibold text-gray-400 w-24 shrink-0">E-posta:</span>
                <a href="mailto:info@mekanname.com" className="text-blue-400 hover:underline transition-colors">info@mekanname.com</a>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-semibold text-gray-400 w-24 shrink-0">Telefon:</span>
                <span>+90 (5XX) XXX XX XX</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-semibold text-gray-400 w-24 shrink-0">Adres:</span>
                <span>Tarihi Yarımada, Fatih, İstanbul, Türkiye</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-semibold text-gray-400 w-24 shrink-0">Sosyal Medya:</span>
                <div className="flex-1 flex gap-2 flex-wrap">
                  <a href="https://twitter.com/mekanname" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline transition-colors">Twitter</a>
                  <span>|</span>
                  <a href="https://instagram.com/mekanname" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline transition-colors">Instagram</a>
                  <span>|</span>
                  <a href="https://facebook.com/mekanname" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline transition-colors">Facebook</a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* --- Footer --- */}
      <footer className="text-center py-2 text-gray-400 text-sm  border-gray-700 mt-12">
      </footer>

      {/* --- Mesaj Durum Modalı --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm p-4" onClick={closeModal}>
          <div 
            className={`w-full max-w-md p-8 rounded-3xl shadow-2xl transform transition-all ${
              modalMessage.type === 'success' ? 'bg-gray-800 text-gray-50 border border-green-500' : 'bg-gray-800 text-gray-50 border border-red-500'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center">
              <div className="mb-4">
                {modalMessage.type === 'success' ? <CheckCircle size={48} className="text-green-500" /> : <XCircle size={48} className="text-red-500" />}
              </div>
              <h3 className="text-2xl font-bold mb-2">{modalMessage.type === 'success' ? "Başarılı!" : "Hata!"}</h3>
              <p className="text-gray-300">{modalMessage.text}</p>
              <button onClick={closeModal} className="mt-6 px-6 py-2 bg-blue-500 text-white font-semibold rounded-full hover:bg-blue-600 transition-colors duration-200">
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactPage;